// Loader and renderer for models cooked by tools/ufbx/cook.c (see
// unity-overview.md §6): a JSON manifest plus a flat f32 binary blob with
// non-indexed triangle-corner geometry in node-local space and animation
// clips as per-frame node TRS tracks.

import { createProgram } from "./webgl";
import { modelVS, modelFS } from "./shaders";
import { composeTrs, multiply, type Mat4 } from "./math";

interface NodeJson {
  name: string;
  parent: number;
  t: number[];
  q: number[];
  s: number[];
}

interface PartJson {
  node: number;
  vertexCount: number;
  posOffset: number;
  nrmOffset: number;
  uvOffset: number;
  material?: string;
  color?: number[];
  texture?: string;
}

export interface AnimClip {
  name: string;
  duration: number;
  fps: number;
  frames: number;
  offset: number;
}

export interface ModelJson {
  name: string;
  bin: string;
  nodes: NodeJson[];
  parts: PartJson[];
  anims: AnimClip[];
}

export interface ModelPart {
  vao: WebGLVertexArrayObject;
  vertexCount: number;
  node: number;
  color: [number, number, number];
  texture: WebGLTexture | null;
}

/** Rest-pose AABB in composed model space (node worlds applied). */
export interface ModelBounds {
  readonly min: readonly [number, number, number];
  readonly max: readonly [number, number, number];
}

export interface CookedModel {
  name: string;
  nodes: NodeJson[];
  order: number[]; // node indices, parents before children
  parts: ModelPart[];
  anims: Map<string, AnimClip>;
  data: Float32Array; // the whole bin
  bounds: ModelBounds;
}

export interface ModelSource {
  // The manifest is imported as a JSON module (Parcel would transform a
  // fetched .json into a JS bundle); only bin and textures are fetched.
  manifest: ModelJson;
  bin: URL;
  textures: Record<string, URL>;
}

export async function loadTexture(
  gl: WebGL2RenderingContext,
  url: URL,
  into?: WebGLTexture,
): Promise<WebGLTexture> {
  const blob = await (await fetch(url.href)).blob();
  const img = await createImageBitmap(blob, { imageOrientation: "flipY" });
  const tex = into ?? gl.createTexture();
  if (!tex) throw new Error("Failed to create texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
  gl.generateMipmap(gl.TEXTURE_2D);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  img.close();
  return tex;
}

function partVao(
  gl: WebGL2RenderingContext,
  data: Float32Array,
  part: PartJson,
): WebGLVertexArrayObject {
  const vao = gl.createVertexArray();
  if (!vao) throw new Error("Failed to create VAO");
  gl.bindVertexArray(vao);
  const attribs: [number, number, number][] = [
    // location, float offset, components
    [0, part.posOffset / 4, 3],
    [1, part.nrmOffset / 4, 3],
    [2, part.uvOffset / 4, 2],
  ];
  for (const [location, offset, size] of attribs) {
    const buf = gl.createBuffer();
    if (!buf) throw new Error("Failed to create buffer");
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      data.subarray(offset, offset + part.vertexCount * size),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, size, gl.FLOAT, false, 0, 0);
  }
  gl.bindVertexArray(null);
  return vao;
}

/**
 * Rest-pose AABB over every part's vertices, node worlds applied — computed
 * while the vertex data is client-side, so menu picking (plan 17) can project
 * 8 corners instead of re-reading geometry. Pure over the manifest + bin, so
 * node-side tests can feed it `fs`-read files without a GL context.
 */
export function restPoseBounds(json: ModelJson, data: Float32Array): ModelBounds {
  const worlds = new Array<Mat4>(json.nodes.length);
  for (const i of hierarchyOrder(json.nodes)) {
    const node = json.nodes[i];
    const local = composeTrs(node.t, node.q, node.s);
    worlds[i] = node.parent < 0 ? local : multiply(worlds[node.parent], local);
  }
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (const part of json.parts) {
    const m = worlds[part.node];
    const base = part.posOffset / 4;
    for (let v = 0; v < part.vertexCount; v++) {
      const x = data[base + v * 3];
      const y = data[base + v * 3 + 1];
      const z = data[base + v * 3 + 2];
      const wx = m[0] * x + m[4] * y + m[8] * z + m[12];
      const wy = m[1] * x + m[5] * y + m[9] * z + m[13];
      const wz = m[2] * x + m[6] * y + m[10] * z + m[14];
      if (wx < min[0]) min[0] = wx;
      if (wy < min[1]) min[1] = wy;
      if (wz < min[2]) min[2] = wz;
      if (wx > max[0]) max[0] = wx;
      if (wy > max[1]) max[1] = wy;
      if (wz > max[2]) max[2] = wz;
    }
  }
  return { min, max };
}

// Traversal order with every parent before its children; node order in the
// cooked file follows the FBX file and does not guarantee this.
function hierarchyOrder(nodes: NodeJson[]): number[] {
  const order: number[] = [];
  const placed = new Array<boolean>(nodes.length).fill(false);
  while (order.length < nodes.length) {
    for (let i = 0; i < nodes.length; i++) {
      if (placed[i]) continue;
      const p = nodes[i].parent;
      if (p < 0 || placed[p]) {
        order.push(i);
        placed[i] = true;
      }
    }
  }
  return order;
}

export async function loadCookedModel(
  gl: WebGL2RenderingContext,
  source: ModelSource,
): Promise<CookedModel> {
  const json = source.manifest;
  const bin = await fetch(source.bin.href).then((r) => r.arrayBuffer());
  const data = new Float32Array(bin);

  const textures = new Map<string, WebGLTexture>();
  for (const part of json.parts) {
    if (!part.texture || textures.has(part.texture)) continue;
    const url = source.textures[part.texture];
    if (!url) {
      console.warn(`model ${json.name}: no URL provided for texture ${part.texture}`);
      continue;
    }
    textures.set(part.texture, await loadTexture(gl, url));
  }

  return {
    name: json.name,
    nodes: json.nodes,
    order: hierarchyOrder(json.nodes),
    parts: json.parts.map((p) => ({
      vao: partVao(gl, data, p),
      vertexCount: p.vertexCount,
      node: p.node,
      color: (p.color?.slice(0, 3) ?? [0.8, 0.8, 0.8]) as [number, number, number],
      texture: (p.texture && textures.get(p.texture)) || null,
    })),
    anims: new Map(json.anims.map((a) => [a.name, a])),
    data,
    bounds: restPoseBounds(json, data),
  };
}

const tmpT = new Float32Array(3);
const tmpQ = new Float32Array(4);
const tmpS = new Float32Array(3);

// World matrix per node for a clip at `time`. Sampled TRS frames are lerped
// (rotation nlerp'd) and composed down the hierarchy. `loop` wraps `time` into
// the clip; a non-looping clip (die) clamps and holds its last frame instead.
export function poseWorlds(
  model: CookedModel,
  clip: string,
  time: number,
  loop: boolean,
): Mat4[] {
  const anim = model.anims.get(clip);
  const worlds = new Array<Mat4>(model.nodes.length);
  const n = model.nodes.length;

  let f0 = 0;
  let f1 = 0;
  let alpha = 0;
  if (anim) {
    const wrapped = ((time % anim.duration) + anim.duration) % anim.duration;
    const clamped = time < 0 ? 0 : time > anim.duration ? anim.duration : time;
    const t = anim.duration > 0 ? (loop ? wrapped : clamped) : 0;
    const f = t * anim.fps;
    f0 = Math.min(Math.floor(f), anim.frames - 1);
    f1 = Math.min(f0 + 1, anim.frames - 1);
    alpha = f - f0;
  }

  for (const i of model.order) {
    const node = model.nodes[i];
    let local: Mat4;
    if (anim) {
      const a = anim.offset / 4 + (f0 * n + i) * 10;
      const b = anim.offset / 4 + (f1 * n + i) * 10;
      const d = model.data;
      for (let k = 0; k < 3; k++) tmpT[k] = d[a + k] + (d[b + k] - d[a + k]) * alpha;
      // nlerp, negating the second quat if the dot is negative (shortest arc)
      let dot = 0;
      for (let k = 0; k < 4; k++) dot += d[a + 3 + k] * d[b + 3 + k];
      const sign = dot < 0 ? -1 : 1;
      let len = 0;
      for (let k = 0; k < 4; k++) {
        tmpQ[k] = d[a + 3 + k] + (sign * d[b + 3 + k] - d[a + 3 + k]) * alpha;
        len += tmpQ[k] * tmpQ[k];
      }
      len = Math.sqrt(len) || 1;
      for (let k = 0; k < 4; k++) tmpQ[k] /= len;
      for (let k = 0; k < 3; k++) tmpS[k] = d[a + 7 + k] + (d[b + 7 + k] - d[a + 7 + k]) * alpha;
      local = composeTrs(tmpT, tmpQ, tmpS);
    } else {
      local = composeTrs(node.t, node.q, node.s);
    }
    worlds[i] = node.parent < 0 ? local : multiply(worlds[node.parent], local);
  }
  return worlds;
}

export class ModelRenderer {
  private readonly program: WebGLProgram;
  private readonly uMvp: WebGLUniformLocation | null;
  private readonly uModel: WebGLUniformLocation | null;
  private readonly uColor: WebGLUniformLocation | null;
  private readonly uUseTexture: WebGLUniformLocation | null;
  private readonly uAlphaCutout: WebGLUniformLocation | null;

  constructor(gl: WebGL2RenderingContext) {
    this.program = createProgram(gl, modelVS, modelFS);
    this.uMvp = gl.getUniformLocation(this.program, "uMvp");
    this.uModel = gl.getUniformLocation(this.program, "uModel");
    this.uColor = gl.getUniformLocation(this.program, "uColor");
    this.uUseTexture = gl.getUniformLocation(this.program, "uUseTexture");
    this.uAlphaCutout = gl.getUniformLocation(this.program, "uAlphaCutout");
    gl.useProgram(this.program);
    gl.uniform1i(gl.getUniformLocation(this.program, "uTexture"), 0);
    gl.useProgram(null);
  }

  // placement: model-to-world for the whole model (on top of node worlds).
  // alphaCutout: discard texels below 0.5 alpha — the menu text planes only.
  draw(
    gl: WebGL2RenderingContext,
    model: CookedModel,
    worlds: Mat4[],
    placement: Mat4,
    viewProj: Mat4,
    alphaCutout = false,
  ): void {
    gl.useProgram(this.program);
    gl.disable(gl.CULL_FACE); // legacy meshes have mixed winding / thin parts
    gl.uniform1i(this.uAlphaCutout, alphaCutout ? 1 : 0);
    gl.activeTexture(gl.TEXTURE0);
    for (const part of model.parts) {
      const world = multiply(placement, worlds[part.node]);
      gl.uniformMatrix4fv(this.uModel, false, world);
      gl.uniformMatrix4fv(this.uMvp, false, multiply(viewProj, world));
      gl.uniform3fv(this.uColor, part.color);
      gl.uniform1i(this.uUseTexture, part.texture ? 1 : 0);
      if (part.texture) gl.bindTexture(gl.TEXTURE_2D, part.texture);
      gl.bindVertexArray(part.vao);
      gl.drawArrays(gl.TRIANGLES, 0, part.vertexCount);
    }
    gl.bindVertexArray(null);
    gl.enable(gl.CULL_FACE);
  }
}
